import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  ChartBarIcon,
  CloudArrowDownIcon,
  SparklesIcon,
  CalendarIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import {
  analyticsApi,
  AnalyticsFilters,
  AnalyticsMetricType,
  AnalyticsPlatform,
  DateBucket,
  ComparisonType,
  AudienceDimensionType,
} from '@/services/analyticsApi';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';

const ALL_PLATFORMS: AnalyticsPlatform[] = [
  'youtube',
  'instagram',
  'tiktok',
  'linkedin',
  'facebook',
  'pinterest',
  'x',
  'all',
];

const METRIC_OPTIONS: { value: AnalyticsMetricType; label: string }[] = [
  { value: 'views', label: 'Views' },
  { value: 'watchTime', label: 'Watch Time' },
  { value: 'likes', label: 'Likes' },
  { value: 'comments', label: 'Comments' },
  { value: 'shares', label: 'Shares' },
  { value: 'subscribers', label: 'Subscribers' },
  { value: 'followers', label: 'Followers' },
  { value: 'reach', label: 'Reach' },
  { value: 'impressions', label: 'Impressions' },
  { value: 'ctr', label: 'CTR' },
  { value: 'retention', label: 'Retention' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'rpm', label: 'RPM' },
  { value: 'cpm', label: 'CPM' },
  { value: 'affiliateSales', label: 'Affiliate Sales' },
];

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#6366f1', '#14b8a6'];

function formatNumber(n: number | string | null | undefined): string {
  const value = Number(n || 0);
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toLocaleString();
}

function buildPeriodDates(period: AnalyticsFilters['period']): { start?: string; end?: string } {
  const end = new Date();
  const start = new Date();
  switch (period) {
    case 'daily':
      start.setDate(end.getDate() - 1);
      break;
    case 'weekly':
      start.setDate(end.getDate() - 7);
      break;
    case 'yearly':
      start.setFullYear(end.getFullYear() - 1);
      break;
    case 'monthly':
    default:
      start.setMonth(end.getMonth() - 1);
      break;
  }
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
}

const MetricCard: React.FC<{ label: string; value: string; change?: number | null }> = ({
  label,
  value,
  change,
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
    <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
    <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
    {change !== null && change !== undefined && (
      <p className={`text-sm mt-1 ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
        {change >= 0 ? '+' : ''}
        {change.toFixed(1)}%
      </p>
    )}
  </div>
);

export const AnalyticsDashboardPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState<AnalyticsFilters['period']>('monthly');
  const [platforms, setPlatforms] = useState<AnalyticsPlatform[]>(['all']);
  const [bucket, setBucket] = useState<DateBucket>('day');
  const [timeSeriesMetrics, setTimeSeriesMetrics] = useState<AnalyticsMetricType[]>(['views', 'likes']);
  const [comparisonType, setComparisonType] = useState<ComparisonType>('platform');
  const [audienceDimension, setAudienceDimension] = useState<AudienceDimensionType>('region');
  const [customDates, setCustomDates] = useState<{ start?: string; end?: string }>({});

  const dates = useMemo(() => {
    if (period === 'custom') return customDates;
    return buildPeriodDates(period);
  }, [period, customDates]);

  const filters: AnalyticsFilters = useMemo(
    () => ({
      period,
      ...dates,
      platforms,
    }),
    [period, dates, platforms]
  );

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['analytics-overview', filters],
    queryFn: () => analyticsApi.getOverview(filters),
  });

  const { data: timeSeries, isLoading: seriesLoading } = useQuery({
    queryKey: ['analytics-time-series', filters, bucket, timeSeriesMetrics],
    queryFn: () => analyticsApi.getTimeSeries(timeSeriesMetrics, bucket, filters),
  });

  const { data: comparison, isLoading: comparisonLoading } = useQuery({
    queryKey: ['analytics-comparison', filters, comparisonType],
    queryFn: () => analyticsApi.getComparison(comparisonType, filters),
  });

  const { data: audience, isLoading: audienceLoading } = useQuery({
    queryKey: ['analytics-audience', filters, audienceDimension],
    queryFn: () => analyticsApi.getAudience(audienceDimension, filters),
  });

  const { data: insights, isLoading: insightsLoading } = useQuery({
    queryKey: ['analytics-insights', filters],
    queryFn: () => analyticsApi.getInsights(filters),
  });

  const [syncing, setSyncing] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await analyticsApi.syncYouTube(dates.start, dates.end);
      if (result.accounts.length === 0) {
        toast.error(result.message);
        return;
      }
      const errors = result.accounts
        .filter((a) => a.error)
        .map((a) => `${a.channelTitle}: ${a.error}`)
        .join('\n');
      if (errors) {
        toast.error(`YouTube sync completed with errors:\n${errors}`, { duration: 8000 });
      } else {
        toast.success(`Synced ${result.synced} metric rows for YouTube`);
      }
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
    } catch (err: any) {
      toast.error(`YouTube sync failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setSyncing(false);
    }
  };

  const totals = overview?.data?.totals;

  const toggleMetric = (metric: AnalyticsMetricType) => {
    setTimeSeriesMetrics((prev) =>
      prev.includes(metric) ? prev.filter((m) => m !== metric) : [...prev, metric]
    );
  };

  const onPlatformChange = (platform: AnalyticsPlatform) => {
    if (platform === 'all') {
      setPlatforms(['all']);
      return;
    }
    setPlatforms((prev) => {
      const filtered = prev.filter((p) => p !== 'all');
      if (filtered.includes(platform)) return filtered.filter((p) => p !== platform);
      return [...filtered, platform];
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ChartBarIcon className="h-7 w-7 text-blue-600" />
            Analytics
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Unified metrics, comparisons and AI-powered insights across your platforms.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSync}
            disabled={syncing}
            className="px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            <CloudArrowDownIcon className="h-4 w-4" />
            {syncing ? 'Syncing...' : 'Sync YouTube'}
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">Filters</span>
          </div>
          <div className="flex flex-wrap gap-4 flex-1">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as AnalyticsFilters['period'])}
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm px-3 py-2"
            >
              <option value="daily">Last 24 hours</option>
              <option value="weekly">Last 7 days</option>
              <option value="monthly">Last 30 days</option>
              <option value="yearly">Last year</option>
              <option value="custom">Custom</option>
            </select>
            {period === 'custom' && (
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  value={customDates.start || ''}
                  onChange={(e) => setCustomDates((d) => ({ ...d, start: e.target.value }))}
                  className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm px-3 py-2"
                />
                <input
                  type="date"
                  value={customDates.end || ''}
                  onChange={(e) => setCustomDates((d) => ({ ...d, end: e.target.value }))}
                  className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm px-3 py-2"
                />
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {ALL_PLATFORMS.map((p) => (
                <button
                  key={p}
                  onClick={() => onPlatformChange(p)}
                  className={`px-3 py-1 text-xs rounded-full border transition ${
                    platforms.includes(p)
                      ? 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900 dark:text-blue-200'
                      : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {overviewLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-24 bg-gray-100 dark:bg-gray-700 animate-pulse rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {METRIC_OPTIONS.map((m) => {
            const total = totals?.[m.value];
            return (
              <MetricCard
                key={m.value}
                label={m.label}
                value={formatNumber(total?.value ?? 0)}
                change={total?.change ?? null}
              />
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Time series</h3>
            <div className="flex items-center gap-2">
              <select
                value={bucket}
                onChange={(e) => setBucket(e.target.value as DateBucket)}
                className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm px-3 py-1"
              >
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
                <option value="year">Year</option>
              </select>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {METRIC_OPTIONS.map((m) => (
              <button
                key={m.value}
                onClick={() => toggleMetric(m.value)}
                className={`px-2 py-1 text-xs rounded border ${
                  timeSeriesMetrics.includes(m.value)
                    ? 'bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-900 dark:text-blue-200'
                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="h-72">
            {seriesLoading || !timeSeries?.data?.length ? (
              <div className="h-full flex items-center justify-center text-gray-500 text-sm">No time series data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timeSeries.data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  {timeSeriesMetrics.map((metric, idx) => (
                    <Line
                      key={metric}
                      type="monotone"
                      dataKey={metric}
                      name={METRIC_OPTIONS.find((o) => o.value === metric)?.label || metric}
                      stroke={COLORS[idx % COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Comparison</h3>
            <select
              value={comparisonType}
              onChange={(e) => setComparisonType(e.target.value as ComparisonType)}
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm px-3 py-1"
            >
              <option value="platform">Platform</option>
              <option value="content">Content</option>
              <option value="campaign">Campaign</option>
              <option value="creator">Creator</option>
            </select>
          </div>
          <div className="h-72">
            {comparisonLoading || !comparison?.data?.length ? (
              <div className="h-full flex items-center justify-center text-gray-500 text-sm">No comparison data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={comparison.data.slice(0, 8)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={0} angle={-20} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="views" name="Views" fill="#3b82f6" />
                  <Bar dataKey="engagement" name="Engagement" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Audience</h3>
            <select
              value={audienceDimension}
              onChange={(e) => setAudienceDimension(e.target.value as AudienceDimensionType)}
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm px-3 py-1"
            >
              <option value="region">Region</option>
              <option value="language">Language</option>
              <option value="ageGroup">Age Group</option>
              <option value="gender">Gender</option>
              <option value="device">Device</option>
              <option value="topic">Topic</option>
              <option value="hashtag">Hashtag</option>
              <option value="interest">Interest</option>
            </select>
          </div>
          <div className="h-72">
            {audienceLoading || !audience?.data?.length ? (
              <div className="h-full flex items-center justify-center text-gray-500 text-sm">No audience data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip />
                  <Legend />
                  <Pie
                    data={audience.data.slice(0, 8)}
                    dataKey="views"
                    nameKey="value"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label
                  >
                    {audience.data.slice(0, 8).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <SparklesIcon className="h-5 w-5 text-purple-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">AI Insights</h3>
          </div>
          {insightsLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 animate-pulse rounded-lg" />
              ))}
            </div>
          ) : !insights?.data?.length ? (
            <p className="text-sm text-gray-500">No insights yet. Sync or seed demo data to generate AI insights.</p>
          ) : (
            <div className="space-y-3">
              {insights.data.map((insight, idx) => (
                <div
                  key={idx}
                  className="p-3 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <p className="font-medium text-gray-900 dark:text-white">{insight.title}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{insight.description}</p>
                  {insight.value !== undefined && (
                    <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 mt-1">{formatNumber(insight.value)}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top content</h3>
        {overviewLoading || !overview?.data?.topContent?.length ? (
          <p className="text-sm text-gray-500">No content data available.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-2 font-medium text-gray-700 dark:text-gray-200">Content</th>
                  <th className="px-4 py-2 font-medium text-gray-700 dark:text-gray-200">Platform</th>
                  <th className="px-4 py-2 font-medium text-gray-700 dark:text-gray-200 text-right">Views</th>
                  <th className="px-4 py-2 font-medium text-gray-700 dark:text-gray-200 text-right">Engagement</th>
                </tr>
              </thead>
              <tbody>
                {overview.data.topContent.map((item) => (
                  <tr key={item.contentId} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="px-4 py-2 text-gray-900 dark:text-white font-medium truncate max-w-xs">
                      {item.contentTitle || item.contentId}
                    </td>
                    <td className="px-4 py-2 text-gray-500 dark:text-gray-400 capitalize">{item.platform}</td>
                    <td className="px-4 py-2 text-right text-gray-900 dark:text-white">{formatNumber(item.views)}</td>
                    <td className="px-4 py-2 text-right text-gray-900 dark:text-white">{formatNumber(item.engagement)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboardPage;
